import { useCallback, useEffect, useState } from "react";
import { MainContainer } from "./styles";
import { PageContainer } from "components/PageContainer";
import { Toolbar } from "components/Toolbar";
import { Box, Typography, LinearProgress, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Container, StyledMainAccordion, StyledAccordionDetails } from "components/accordion/styles";
import { IPunctualityResponse, IPunctualityOrder, IPunctualityParams } from "interfaces/IPunctuality";
import { dashboardService } from "services/dashboardService";
import { PunctualityTable } from "./components/Table";
import { GroupSummary } from "./components/GroupSummary";
import { toast } from "react-toastify";
import PageLoader from "components/Table/PageLoader";
import { NoDataMessage } from "components/NoDataMessage/styles";
import { DashboardPaginationProps } from "interfaces/IPagination";
import { CustomPagination } from "components/CustomPagination";
import { dateToday } from "utils/dateToday";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ActiveFilters } from "components/ActiveFilters";
import dayjs from "dayjs";

// Tipos de ordenação movidos para o componente da tabela

// Estado para gerenciar detalhes de cada acordeão
interface AccordionDetail {
  [groupName: string]: {
    data: DashboardPaginationProps<IPunctualityOrder>;
    loading: boolean;
    isFirstLoad: boolean; // Para distinguir primeiro carregamento de atualizações
    currentPage: number;
    groupId: number; // ID do grupo para usar nas requisições
    orderBy?: 'dt' | 'delay' | 'delivery_date' | 'city';
    orderDirection?: 'asc' | 'desc';
  };
}

export const Punctuality = () => {
  // Estado principal: dados de resumo dos grupos (endpoint principal)
  const [punctualityData, setPunctualityData] = useState<DashboardPaginationProps<IPunctualityResponse>>(
    {} as DashboardPaginationProps<IPunctualityResponse>
  );

  // Estado para detalhes de cada acordeão (carregados sob demanda)
  const [accordionDetails, setAccordionDetails] = useState<AccordionDetail>({});

  // Estados de loading
  const [loading, setLoading] = useState(false);

  // Removido: sorting agora é gerenciado individualmente por cada acordeão

  // Gerenciamento de parâmetros de URL
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [params, setParams] = useState<IPunctualityParams>(() => {
    const startDate = searchParams.get("startDate") || dateToday();
    const endDate = searchParams.get("endDate") || dateToday();
    const page = Number(searchParams.get("page")) || 1;
    const order = searchParams.get("order") || "desc";
    const search = searchParams.get("search") || "";
    const perPage = Number(searchParams.get("perPage")) || 10;
    const transshipPointsIdsFromUrl = searchParams.getAll("transshipPointsIds");
    const groupByFromUrl = searchParams.get("groupBy");

    return {
      startDate,
      endDate,
      page,
      perPage,
      order,
      search,
      transshipPointsIds: transshipPointsIdsFromUrl.map((id: string) => parseInt(id)).filter((id: number) => !isNaN(id)) || [],
      groupBy: groupByFromUrl || "driver",
    }
  });

  const onChangeParams = (newParamsOrFn: Function | typeof params) => {
    const updatedParams = typeof newParamsOrFn === 'function' ? newParamsOrFn(params) : newParamsOrFn;
    setParams(updatedParams);

    const currentParams = Object.fromEntries(searchParams.entries());
    const combinedParams = { ...currentParams, ...updatedParams };
    setSearchParams(combinedParams as any);
  };

  // Mapeador de filtros para ActiveFilters
  const filterMapper = {
    startDate: {
      label: "Data início",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
    },
    endDate: {
      label: "Data fim",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate, // Oculta se for período de um dia
    },
    transshipPointsIds: {
      label: "Centros de Distribuição",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.transshipPointsIds || params.transshipPointsIds.length === 0,
    },
    groupBy: {
      label: "Agrupamento",
      formatValue: (value: string) => value === 'driver' ? 'Motorista' : 'Time',
      hidden: params.groupBy === 'driver', // Oculta se for o padrão
    },
    order: {
      label: "Ordenação",
      formatValue: (value: string) => value === 'desc' ? 'Maior pontualidade' : 'Menor pontualidade',
      hidden: params.order === 'desc', // Oculta se for o padrão
    },
  };

  // Função para remover filtro específico
  const handleRemoveFilter = (filterKey: string) => {
    const newParams = { ...params };

    if (filterKey in newParams) {
      if (filterKey === 'transshipPointsIds') {
        (newParams as any)[filterKey] = [];
      } else if (filterKey === 'groupBy') {
        (newParams as any)[filterKey] = 'driver';
      } else if (filterKey === 'order') {
        (newParams as any)[filterKey] = 'desc';
      } else if (filterKey === 'startDate' || filterKey === 'endDate') {
        const today = dateToday();
        (newParams as any)['startDate'] = today;
        (newParams as any)['endDate'] = today;
      } else if (filterKey === 'search') {
        (newParams as any)[filterKey] = '';
      }

      setParams(newParams);

      // Atualiza URL
      const newSearchParams = new URLSearchParams();
      Object.entries(newParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => newSearchParams.append(key, v.toString()));
        } else if (value) {
          newSearchParams.set(key, value.toString());
        }
      });
      setSearchParams(newSearchParams);
    }
  };

  // Função para limpar todos os filtros
  const handleClearAllFilters = () => {
    const clearedParams = {
      startDate: dateToday(),
      endDate: dateToday(),
      transshipPointsIds: [],
      groupBy: "driver",
      order: "desc",
      page: 1,
      perPage: 10,
      search: '', // Limpa a busca
    };

    setParams(clearedParams);

    // Atualiza URL
    const newSearchParams = new URLSearchParams();
    if (clearedParams.search) {
      newSearchParams.set('search', clearedParams.search);
    }
    newSearchParams.set('startDate', clearedParams.startDate);
    newSearchParams.set('endDate', clearedParams.endDate);
    newSearchParams.set('groupBy', clearedParams.groupBy);
    newSearchParams.set('order', clearedParams.order);
    newSearchParams.set('page', clearedParams.page.toString());
    newSearchParams.set('perPage', clearedParams.perPage.toString());

    setSearchParams(newSearchParams);
  };

  // Busca dados principais (resumos dos grupos)
  const fetchPunctualityData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getPunctualityData(params);
      setPunctualityData(response);
    } catch (error) {
      toast.error('Erro ao buscar dados de pontualidade');
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Busca detalhes de um grupo específico (quando acordeão é expandido)
  const fetchGroupDetails = useCallback(async (
    groupName: string,
    groupId: number,
    page: number = 1,
    orderBy?: 'dt' | 'delay' | 'delivery_date' | 'city',
    orderDirection?: 'asc' | 'desc'
  ) => {
    try {
      // Mantém ordenação anterior se não especificada
      const currentDetail = accordionDetails[groupName];
      const finalOrderBy = orderBy || currentDetail?.orderBy || 'delivery_date';
      const finalOrderDirection = orderDirection || currentDetail?.orderDirection || 'desc';

      // Verifica se é o primeiro carregamento (sem dados ainda)
      const isFirstLoad = !currentDetail?.data;

      // Marca este grupo como carregando
      setAccordionDetails(prev => ({
        ...prev,
        [groupName]: {
          ...prev[groupName],
          loading: true,
          isFirstLoad,
          currentPage: page,
          groupId,
          orderBy: finalOrderBy,
          orderDirection: finalOrderDirection
        }
      }));

      const detailParams = {
        ...params,
        groupId, // Usa o groupId para filtrar
        page,
        perPage: 10, // Paginação específica para detalhes
        orderBy: finalOrderBy,
        orderDirection: finalOrderDirection
      };

      const response = await dashboardService.getPunctualityDetail(detailParams);

      setAccordionDetails(prev => ({
        ...prev,
        [groupName]: {
          data: response,
          loading: false,
          isFirstLoad: false,
          currentPage: page,
          groupId,
          orderBy: finalOrderBy,
          orderDirection: finalOrderDirection
        }
      }));
    } catch (error) {
      toast.error(`Erro ao buscar detalhes do grupo ${groupName}`);
      setAccordionDetails(prev => ({
        ...prev,
        [groupName]: {
          ...prev[groupName],
          loading: false,
          isFirstLoad: false
        }
      }));
    }
  }, [params, accordionDetails]);

  useEffect(() => {
    fetchPunctualityData();
    // Limpa detalhes dos acordeões quando os parâmetros principais mudam
    setAccordionDetails({});
  }, [fetchPunctualityData]);

  // Função para lidar com ordenação de uma tabela específica
  const handleTableSort = (groupName: string, orderBy: 'dt' | 'delay' | 'delivery_date' | 'city', orderDirection: 'asc' | 'desc') => {
    // Sempre volta para a página 1 quando ordena
    const groupDetail = accordionDetails[groupName];
    if (groupDetail?.groupId) {
      fetchGroupDetails(groupName, groupDetail.groupId, 1, orderBy, orderDirection);
    }
  };

  return (
    <div className="pageContent">
      <PageContainer>
        <MainContainer>
          <div className='infos'>
            <h3>Pontualidade</h3>
            <Toolbar
              params={params}
              paramState={onChangeParams}
              loadingState={loading}
              placeholder={params.groupBy === 'driver' ? 'Motorista' : 'Time'}
              page="punctuality"
            />
          </div>

          <ActiveFilters
            params={params}
            mapper={filterMapper}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            title="Filtros aplicados"
            hideWhenEmpty={true}
          />
          {loading ? (
            <PageLoader height='35vh' />
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              {punctualityData?.items?.length > 0 ? (
                <>
                  {/* Acordeões com carregamento dinâmico */}
                  <Container>
                    {punctualityData.items.map((group, index) => {
                      const groupDetail = accordionDetails[group.groupName];
                      const displayName = group.driverName || group.groupName;

                      return (
                        <StyledMainAccordion
                          key={index}
                          onChange={(_, isExpanded) => {
                            if (isExpanded && !groupDetail?.data) {
                              fetchGroupDetails(group.groupName, group.id);
                            }
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-content-${index}`}
                            id={`panel-header-${index}`}
                          >
                            <Box display="flex" alignItems="center" width="100%" justifyContent={"space-between"}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.1rem' },
                                  fontWeight: 600,
                                  color: 'rgba(0, 0, 0, 0.87)'
                                }}
                              >
                                {displayName}
                              </Typography>
                              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                                <GroupSummary group={group} />
                              </Box>
                            </Box>
                          </AccordionSummary>

                          <StyledAccordionDetails>
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                              <GroupSummary group={group} isMobile />
                            </Box>

                            {/* Conteúdo do acordeão */}
                            {groupDetail?.loading && groupDetail?.isFirstLoad ? (
                              // Primeiro carregamento: apenas loading
                              <Box sx={{ width: '100%', mt: 2 }}>
                                <LinearProgress />
                                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                  Carregando detalhes...
                                </Typography>
                              </Box>
                            ) : groupDetail?.data?.items ? (
                              // Tem dados: mostra tabela (com loading embaixo do cabeçalho da tabela se estiver carregando)
                              <>
                                <PunctualityTable
                                  orders={groupDetail.data.items}
                                  currentOrderBy={groupDetail.orderBy}
                                  currentOrderDirection={groupDetail.orderDirection}
                                  onSort={(orderBy, orderDirection) => handleTableSort(group.groupName, orderBy, orderDirection)}
                                  showLoading={groupDetail?.loading && !groupDetail?.isFirstLoad}
                                />

                                {/* Paginação dos detalhes */}
                                {groupDetail.data.totalPages > 1 && (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <CustomPagination
                                      currentPage={groupDetail.currentPage}
                                      totalPages={groupDetail.data.totalPages}
                                      onPageChange={(page) => fetchGroupDetails(group.groupName, group.id, page)}
                                    />
                                  </Box>
                                )}
                              </>
                            ) : (
                              // Nenhum dado ainda: mensagem de instruções
                              <Typography variant="body2" sx={{ textAlign: 'center', p: 2 }}>
                                Expanda para ver os detalhes
                              </Typography>
                            )}
                          </StyledAccordionDetails>
                        </StyledMainAccordion>
                      );
                    })}
                  </Container>
                  {punctualityData?.totalPages && punctualityData.totalPages > 1 && (
                    <CustomPagination
                      currentPage={params.page || 1}
                      totalPages={punctualityData.totalPages}
                      onPageChange={(value) => onChangeParams({ ...params, page: value })}
                    />
                  )}
                </>
              ) : (
                <NoDataMessage ht="35vh">
                  <h3>Não há dados para exibir :(</h3>
                  <p>Verifique os filtros aplicados e tente novamente.</p>
                </NoDataMessage>
              )}
            </Box>
          )}
        </MainContainer>
      </PageContainer>
    </div>
  );
};
